<template>
  <div id="app" class="uk-background-default">
    <v-app>
      <transition name="slide-y-transition" v-if="forbiddenPath()">
        <InternalMenu v-if="isInternalUser"></InternalMenu>
        <ClientHeader v-else class="content"></ClientHeader>
      </transition>

      <v-content
        v-bind:style="{backgroundColor: '#ffffff'}"
        class="pb-0"
        :class="{'client-header-fixed-on-mobile': $vuetify.breakpoint.smAndDown && !isInternalUser}">
        <v-container fluid pa-0>
          <transition name="fade" mode="out-in">
            <router-view class="main-view"></router-view>
          </transition>
        </v-container>
      </v-content>

      <div class="reload-container" v-if="hashChanged">
        <div class="notification-button text-center">
          <p>An update is available. Please save all current work and click update
            below. You can also accept these updates by refreshing your browser at any time.</p>
          <p class="text-center">Not updating may result in errors.</p>
        </div>
        <div class="refresh-button text-center">
          <v-btn text outlined color="indigo" @click="reloadApp">Update</v-btn>
        </div>
      </div>

      <v-footer class="footer">
        &copy;2020 BeCool
      </v-footer>


      <v-snackbar v-model="snackbar" :timeout="3000">
        {{snackbarMessage}}
        <v-btn color="blue darken-1" text @click="snackbar=false">Close</v-btn>
      </v-snackbar>

      <v-dialog
        v-model="dialog"
        max-width="555"
        transition="dialog-bottom-transition" 
      >
        <v-card elevation="4" light> 

          <v-card-title class="pa-3"> 
            <v-icon
              color="info" 
              large       
              left        
            >
              mdi-information </v-icon>
            <v-spacer></v-spacer> </v-card-title>

          <v-divider></v-divider>

          <v-card-text
              class="pa-5 text--primary"  style="font-size: 1.25rem; line-height: 1.5; font-weight: normal;"
            >
            {{ dialogMessage }}
          </v-card-text>

          <v-card-actions class="pa-3"> 
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              large 
              @click="$store.commit('clearDialog')"
            >
              OK
            </v-btn>
          </v-card-actions>

        </v-card>
      </v-dialog>

      <v-dialog v-model="waitDialog" width="500px" persistent style="z-index: 1000">
        <v-card>
          <v-card-title class="justify-center">
            <h2>
              Please wait
            </h2>
          </v-card-title>
          <v-card-text class="justify-center">
            <div class="loader" style="margin-left: auto; margin-right: auto;"></div>
            <div v-if="jobId">
              Job {{ jobId }} is {{ jobStatus }}. Message: {{ jobMessage }}
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

    </v-app>
  </div>
</template>

<script>
    import ClientHeader from '@/components/util/ClientHeader';
    import InternalMenu from '@/components/util/InternalMenu';
    import refreshPageMixin from '@/mixins/refresh-page.mixin.js';


    export default {
        name: 'App',
        components: {
            ClientHeader: ClientHeader,
            InternalMenu: InternalMenu
        },
        mixins: [refreshPageMixin],
        computed: {
            jobId() {
              return this.$store.getters['jobId'];
            },
            jobStatus() {
              return this.$store.getters['jobStatus'];
            },
            jobMessage() {
              return this.$store.getters['jobMessage'];
            },
            snackbar: {
                get() {
                    return this.$store.state.snackbar;
                },
                set(value) {
                    if (value === false) {
                        this.$store.commit('clearSnackbar');
                    }
                }
            },
            waitDialog: {
                get() {
                    return this.$store.state.waitDialog;
                },
            },
            snackbarMessage() {
                return this.$store.state.snackbarMessage;
            },
            isInternalUser() {
                return this.$store.getters['auth/isInternalUser'];
            },
            dialog: {
               get() {
                    return this.$store.state.dialog;
                },
                set(value) {
                    if (value === false) {
                        this.$store.commit('clearDialog');
                    }
                }
            },
            dialogMessage() {
                return this.$store.state.dialogMessage;
            },
        },
        methods: {
            forbiddenPath: function () {
                if (this.$route.name === 'Login' || this.$route.name === 'Callback' || this.$route.name === 'TrackPage'
                    || this.$route.name === 'Create Client' || this.$route.name === 'Register') {
                    return false;
                }
                return true;
            },
        },
        beforeMount() {
            if (this.forbiddenPath()) {
                this.$store.commit('auth/setRedirect', this.$route.fullPath);
            }
            this.initVersionCheck(location.protocol + '//' + location.hostname + ":" + location.port + "/static/version.json");
        }
    }
</script>

<style>
  @import "https://cdn.jsdelivr.net/npm/animate.css@3.5.1";
  @import "./mainStyle.css";

  .reload-container {
    width: 100%;
    padding: 30px 0;
    background-color: #cbebf9;
  }

  .footer {
    display: flex;
    justify-content: center;
    font-size: 12px;
    padding: 20px;
    margin-top: auto;
  }

  .client-header-fixed-on-mobile {
    padding-top: 56px !important;
  }

  .loader {
    border: 5px solid #f3f3f3; /* Light grey */
    border-top: 5px solid #3498db; /* Blue */
    border-radius: 50%;
    width: 80px;
    height: 80px;
    animation: spin 1.5s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }


  ::-webkit-scrollbar {
    width: 15px;  /* Total width including `border-width` of scrollbar thumb */
  }
  ::-webkit-scrollbar-thumb {
    border: 4px solid rgba(0, 0, 0, 0);  /* Transparent border together with `background-clip: padding-box` does the trick */
    background-clip: padding-box;
    border-radius: 1em;
    -webkit-border-radius: 1em;
    background-color: rgba(0, 0, 0, 0.15);
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.025);
    -webkit-box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.025);
  }
  ::-webkit-scrollbar-button {
    width: 0;
    height: 0;
    display: none;
  }
  ::-webkit-scrollbar-corner {
    background-color: transparent;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background-color: #AAAA;
  }

</style>



// WEBPACK FOOTER //
// src/App.vue